// Public testimonial submission form. Lives on the keepsake page. Writes
// via the `submitTestimonial` Cloud Function (admin SDK bypasses rules).
// One submission per orderId is enforced server-side.
//
// UX philosophy: this is not a review form. It's an invitation — a place
// for words to land if any come. Framed as a gifted opportunity (the
// pancake earns the sentences), never an ask or a transaction. Anchor id
// "testimonial" so the aftercare email can deep-link straight here.

import { useEffect, useRef, useState } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

const B = {
  cream:  "#ffdaad",
  gold:   "#fcb31f",
  pink:   "#ef537e",
  sky:    "#4aa4e1",
  dark:   "#0d1833",
};

export default function TestimonialSubmit({ order }) {
  const [text, setText]     = useState("");
  const [name, setName]     = useState("");
  const [honeypot, setHoneypot] = useState(""); // website_url — always empty
  const [sending, setSending] = useState(false);
  const [done, setDone]     = useState(false);
  const [error, setError]   = useState(null);
  const renderedAt = useRef(Date.now());

  // Pre-fill name from recipient if present. We no longer ask where the
  // pancake was made — the venue is derived from the shift on display.
  useEffect(() => {
    if (order?.recipientName && !name) {
      setName(order.recipientName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.recipientName]);

  async function submit(e) {
    e.preventDefault();
    if (sending) return;
    setError(null);
    const trimmed = text.trim();
    if (trimmed.length < 20) {
      setError("A sentence or two, please.");
      return;
    }
    setSending(true);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "submitTestimonial");
      await fn({
        text:           trimmed,
        authorName:     name.trim() || null,
        orderId:        order?.id || null,
        website_url:    honeypot,
        formRenderedAt: renderedAt.current,
      });
      setDone(true);
    } catch (err) {
      const msg = err?.message || "Couldn't submit. Try again in a bit.";
      setError(msg.replace(/^.+\//, ""));  // strip firebase error code prefix
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <div id="testimonial" style={{
        padding: "1.6rem 1.4rem",
        textAlign: "center",
        color: B.cream,
        fontFamily: "var(--jop-display)",
        background: "linear-gradient(180deg, rgba(252,179,31,0.06) 0%, rgba(252,179,31,0) 100%)",
      }}>
        <div style={{
          fontSize: "2rem",
          marginBottom: "0.5rem",
        }}>🥞</div>
        <div style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.45rem", color: B.gold }}>
          Thank you.
        </div>
        <div style={{ fontSize: "0.88rem", color: "rgba(255,218,173,0.78)", lineHeight: 1.6, maxWidth: "26rem", margin: "0 auto" }}>
          We read every one. The ones that move us ride our homepage marquee
          and earn their pancake a pink halo in the Pancakeopedia. Watch for
          yours over the next few days.
        </div>
      </div>
    );
  }

  return (
    <form id="testimonial" onSubmit={submit} style={{
      padding: "1.4rem 1.4rem 1.5rem",
      fontFamily: "var(--jop-display)",
      background: "linear-gradient(180deg, rgba(252,179,31,0.06) 0%, rgba(252,179,31,0) 100%)",
    }}>
      <div style={{
        color: B.gold,
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        fontWeight: 700,
        marginBottom: "0.5rem",
        fontStyle: "italic",
      }}>
        If words come to you
      </div>
      <h3 style={{
        margin: "0 0 0.55rem",
        color: B.cream,
        fontSize: "1.3rem",
        fontWeight: 700,
        lineHeight: 1.2,
      }}>
        Some pancakes earn a few sentences.
      </h3>
      <p style={{
        margin: "0 0 1.1rem",
        color: "rgba(255,218,173,0.72)",
        fontSize: "0.86rem",
        lineHeight: 1.55,
      }}>
        Leave a note about this one. What you write might ride our homepage
        marquee, and your pancake might earn a pink halo in the Pancakeopedia.
        Or it might just live with us — some moments are private, and that's a
        fine answer too.
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="A pancake with my grandma's face on it. I cried a little."
        rows={4}
        maxLength={500}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: "rgba(255,255,255,0.06)",
          border: `1px solid rgba(255,218,173,0.25)`,
          borderRadius: "10px",
          color: B.cream,
          fontFamily: "inherit",
          fontSize: "0.95rem",
          lineHeight: 1.5,
          padding: "0.75rem 0.9rem",
          outline: "none",
          resize: "vertical",
        }}
      />
      <div style={{
        marginTop: "0.35rem",
        color: "rgba(255,218,173,0.4)",
        fontSize: "0.72rem",
        textAlign: "right",
      }}>
        {text.length} / 500
      </div>

      <div style={{ marginTop: "0.8rem" }}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Sign it (optional)"
          maxLength={60}
          style={inputStyle}
        />
      </div>

      {/* Honeypot — bots fill it, humans won't */}
      <input
        type="text"
        name="website_url"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
        aria-hidden="true"
      />

      {error && (
        <div style={{
          marginTop: "0.7rem",
          color: "#ff8a8a",
          fontSize: "0.8rem",
          lineHeight: 1.4,
        }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={sending || text.trim().length < 20}
        style={{
          marginTop: "1rem",
          width: "100%",
          background: sending || text.trim().length < 20 ? "rgba(255,255,255,0.08)" : B.gold,
          color: sending || text.trim().length < 20 ? "rgba(255,255,255,0.35)" : B.dark,
          border: "none",
          borderRadius: "12px",
          padding: "0.85rem",
          fontSize: "0.95rem",
          fontWeight: 700,
          fontFamily: "inherit",
          letterSpacing: "0.02em",
          cursor: sending || text.trim().length < 20 ? "not-allowed" : "pointer",
        }}
      >
        {sending ? "Sending…" : "Send the note"}
      </button>
    </form>
  );
}

const inputStyle = {
  background: "rgba(255,255,255,0.06)",
  border: `1px solid rgba(255,218,173,0.22)`,
  borderRadius: "8px",
  color: B.cream,
  fontFamily: "inherit",
  fontSize: "0.88rem",
  padding: "0.6rem 0.7rem",
  outline: "none",
  boxSizing: "border-box",
  width: "100%",
};
